import { useQuery } from "@tanstack/react-query";

import { useAppSelector } from "src/redux/hooks/redux";
import { selectCurrentCompany } from "src/redux/features/user/selectors/userState.selectors";

import { fetchWorkforcePresence } from "../_queries/workforcePresence.queries";
import { WorkforcePresenceItem } from "../_types/workforcePresence.types";

export const WORKFORCE_PRESENCE_QUERY_KEY = ["workforce-presence"] as const;
const POLL_INTERVAL_MS = 20_000;

export const useWorkforcePresence = (enabled = true) => {
  const companyId = useAppSelector(selectCurrentCompany)?.id;
  const normalizedCompanyId = companyId ? Number(companyId) : null;

  const query = useQuery({
    queryKey: [...WORKFORCE_PRESENCE_QUERY_KEY, normalizedCompanyId],
    queryFn: () => fetchWorkforcePresence(normalizedCompanyId!),
    enabled: enabled && Boolean(normalizedCompanyId),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 10_000,
  });

  const presenceByWorkforceUid = (query.data?.items ?? []).reduce<
    Record<string, WorkforcePresenceItem>
  >((acc, item) => {
    acc[item.workforceUid] = item;
    return acc;
  }, {});

  const presenceByUserUid = (query.data?.items ?? []).reduce<
    Record<string, WorkforcePresenceItem>
  >((acc, item) => {
    if (item.userUid) {
      acc[item.userUid] = item;
    }
    return acc;
  }, {});

  return {
    ...query,
    presenceByWorkforceUid,
    presenceByUserUid,
  };
};

export const getContactPresenceStatus = (
  contact: { type: string; uid: string; userUid?: string },
  presenceByWorkforceUid: Record<string, WorkforcePresenceItem>,
  presenceByUserUid: Record<string, WorkforcePresenceItem>,
): WorkforcePresenceItem["status"] | null => {
  if (contact.type === "workforce") {
    return presenceByWorkforceUid[contact.uid]?.status ?? null;
  }

  if (contact.type === "organizationCoworker") {
    const userUid = contact.userUid ?? contact.uid;
    return presenceByUserUid[userUid]?.status ?? null;
  }

  return null;
};
